import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Lock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const months = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export default function ChangePasswordDialog({ open, onPasswordChanged, isInviteFlow = false }) {
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [birthMonth, setBirthMonth] = useState('');
  const [birthDay, setBirthDay] = useState('');
  const [loading, setLoading] = useState(false);

  const getDaysInMonth = (m) => {
    if (!m) return 31;
    return [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][parseInt(m) - 1];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.newPassword !== formData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (formData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      const payload = { new_password: formData.newPassword };
      if (birthMonth && birthDay) {
        payload.birthday = `${birthMonth.padStart(2, '0')}-${birthDay.padStart(2, '0')}`;
      }
      // Always use set-password — this dialog only shows when must_change_password=true
      await axios.post(`${API}/auth/set-password`, payload);
      toast.success('Password set successfully!');
      onPasswordChanged();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent 
        className="bg-[#18181B] border-[#27272A] max-w-md max-h-[90vh] overflow-y-auto" 
        data-testid="change-password-dialog"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="bg-[#CCFF00]/10 p-3 rounded-sm">
              <Lock className="w-6 h-6 text-[#CCFF00]" />
            </div>
            <div>
              <DialogTitle className="text-2xl font-heading font-black uppercase text-[#FAFAFA]">
                Welcome
              </DialogTitle>
            </div>
          </div>
          <DialogDescription className="text-[#A1A1AA]">
            Set your personal password and enter your birthday so we can celebrate with you!
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          
          <div className="space-y-2">
            <Label htmlFor="new-password" className="text-[#FAFAFA] uppercase text-xs font-semibold">
              New Password
            </Label>
            <Input
              id="new-password"
              data-testid="new-password-input"
              type="password"
              value={formData.newPassword}
              onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
              required
              minLength={6}
              className="bg-[#09090B] border-[#27272A] text-white focus:border-[#CCFF00] h-12"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="confirm-password" className="text-[#FAFAFA] uppercase text-xs font-semibold">
              Confirm New Password
            </Label>
            <Input
              id="confirm-password"
              data-testid="confirm-password-input"
              type="password"
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              required
              minLength={6}
              className="bg-[#09090B] border-[#27272A] text-white focus:border-[#CCFF00] h-12"
            />
          </div>

          <div className="border-t border-[#27272A] pt-4">
            <Label className="text-[#FAFAFA] uppercase text-xs font-semibold mb-3 block">Your Birthday</Label>
            <div className="grid grid-cols-2 gap-3">
              <select
                value={birthMonth}
                onChange={(e) => { setBirthMonth(e.target.value); setBirthDay(''); }}
                data-testid="birthday-month-pw"
                className="w-full bg-[#09090B] border border-[#27272A] text-white rounded-sm h-10 px-3 focus:border-[#CCFF00] focus:outline-none text-sm"
              >
                <option value="">Month</option>
                {months.map((m, i) => (
                  <option key={i} value={String(i + 1)}>{m}</option>
                ))}
              </select>
              <select
                value={birthDay}
                onChange={(e) => setBirthDay(e.target.value)}
                data-testid="birthday-day-pw"
                className="w-full bg-[#09090B] border border-[#27272A] text-white rounded-sm h-10 px-3 focus:border-[#CCFF00] focus:outline-none text-sm"
              >
                <option value="">Day</option>
                {Array.from({ length: getDaysInMonth(birthMonth) }, (_, i) => (
                  <option key={i + 1} value={String(i + 1)}>{i + 1}</option>
                ))}
              </select>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="submit"
              data-testid="submit-password-change"
              disabled={loading}
              className="w-full bg-[#CCFF00] text-black font-bold uppercase tracking-wider hover:bg-[#B3E600] rounded-sm h-12"
            >
              {loading ? 'Saving...' : 'Save & Continue'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
